import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AuthResponse,
  ChangePasswordInput,
  LoginInput,
  RegisterInput,
  SessionResponse,
} from '@biyolink/shared';
import { apiClient, getData, hasSessionCookie, postData } from './client';
import { useAuthStore } from '@/stores/auth-store';

export const authKeys = {
  session: ['auth', 'me'] as const,
};

/** Oturum bilgisi. 401 alınırsa oturumsuz kabul edilir (hata fırlatmaz). */
export function useSession() {
  const setSession = useAuthStore((state) => state.setSession);
  const clearSession = useAuthStore((state) => state.clearSession);

  return useQuery({
    queryKey: authKeys.session,
    queryFn: async (): Promise<SessionResponse | null> => {
      // Oturum işareti yoksa sunucuya sormaya gerek yok (ziyaretçilerin çoğu böyle).
      if (!hasSessionCookie()) {
        clearSession();
        return null;
      }

      try {
        const session = await getData<SessionResponse>('/auth/me');
        setSession(session.user, session.company);
        return session;
      } catch {
        clearSession();
        return null;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Giriş ve kayıt sonrası oturumu store'a DOĞRUDAN yazıyoruz.
 *
 * Yalnızca `invalidateQueries` ile yetinmek güvenilmezdi: oturum düşünce
 * `SessionProvider` `queryClient.clear()` çağırıyor ve `['auth','me']` sorgusu
 * önbellekten siliniyor. Sonraki girişte invalidate geçersiz kılacak bir sorgu
 * bulamıyor, `/auth/me` hiç çağrılmıyor, store boş kalıyor ve `RequireAuth`
 * kullanıcıyı giriş sayfasına geri atıyordu (yenileyince düzeliyordu).
 *
 * Yanıt zaten kullanıcı ve firmayı taşıdığı için ekstra gidiş-dönüşe de gerek yok.
 * Ardından invalidate ediyoruz: yalnızca okunmamış bildirim sayısı tazelensin diye.
 */
function useAuthSuccessHandler() {
  const queryClient = useQueryClient();
  const setSession = useAuthStore((state) => state.setSession);

  return async (auth: AuthResponse) => {
    setSession(auth.user, auth.company);
    await queryClient.invalidateQueries({ queryKey: authKeys.session });
  };
}

export function useLogin() {
  const onAuthenticated = useAuthSuccessHandler();

  return useMutation({
    mutationFn: (input: LoginInput) => postData<AuthResponse>('/auth/login', input),
    onSuccess: onAuthenticated,
  });
}

export function useRegister() {
  const onAuthenticated = useAuthSuccessHandler();

  return useMutation({
    mutationFn: (input: RegisterInput) => postData<AuthResponse>('/auth/register', input),
    onSuccess: onAuthenticated,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const clearSession = useAuthStore((state) => state.clearSession);

  return useMutation({
    mutationFn: () => postData<{ loggedOut: boolean }>('/auth/logout'),
    onSuccess: () => {
      clearSession();
      queryClient.clear();
    },
  });
}

export function useVerifyEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => postData<{ verified: boolean }>('/auth/verify-email', { token }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.session });
    },
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) =>
      postData<{ sent: boolean }>('/auth/resend-verification', { email }),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => postData<{ sent: boolean }>('/auth/forgot-password', { email }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      postData<{ reset: boolean }>('/auth/reset-password', input),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      postData<{ changed: boolean }>('/auth/change-password', input),
  });
}

/** Oturumu sonlandırmadan sunucudan çıkış yapmak gerekirse (interceptor kullanır). */
export async function logoutSilently(): Promise<void> {
  await apiClient.post('/auth/logout').catch(() => undefined);
}
